/**
 * AgentOsDemoManager — V141 "Agent Manager".
 *
 * Competitive intel agent — input own domain + 1-5 competitors, output
 * comparison matrix + weak spots + prioritized action plan to overtake.
 * "Musíme byť #1" packaged as a deterministic JSON-output LLM call.
 */
import { useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Loader2, Crown, Swords, Target } from "lucide-react";
import AgentOsDemoShell from "./AgentOsDemoShell";

const EXAMPLES = [
  { own_domain: "krasneboty.sk", competitor_domains: ["zalando.sk", "deichmann.sk", "humanic.net"],
    own_pitch: "Premium dámska obuv s Made-in-EU príbehom a 60-dňovým vrátením." },
  { own_domain: "supermajstri.sk", competitor_domains: ["mojmajster.sk", "hellomaster.sk"],
    own_pitch: "Sprostredkovanie remeselníkov so zárukou kvality a fixnou cenou pred prácou." },
];

export default function AgentOsDemoManager() {
  const [form, setForm] = useState({
    own_domain: "", competitor_domains: ["", "", "", "", ""], own_pitch: "", lang: "sk",
  });
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: null });

  const loadExample = (i) => {
    const ex = EXAMPLES[i];
    const padded = [...ex.competitor_domains, "", "", "", "", ""].slice(0, 5);
    setForm({ ...form, own_domain: ex.own_domain, own_pitch: ex.own_pitch, competitor_domains: padded });
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.own_domain.trim()) return;
    setStatus({ loading: true, error: null }); setResult(null);
    try {
      const payload = {
        ...form,
        competitor_domains: form.competitor_domains.filter((c) => c.trim()),
      };
      const r = await axios.post(`${API}/agent-os/demo/competitive-manager`, payload);
      setResult(r.data);
      setStatus({ loading: false, error: null });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setStatus({ loading: false, error: typeof d === "string" ? d : (d?.message || "request_failed") });
    }
  };

  const formPanel = (
    <form onSubmit={submit} className="space-y-4 p-6 bg-neutral-950 border border-gray-800" data-testid="manager-form">
      <div className="flex flex-wrap gap-2 mb-2">
        {EXAMPLES.map((e, i) => (
          <button key={i} type="button" onClick={() => loadExample(i)}
            data-testid={`manager-example-${i}`}
            className="text-[11px] text-amber-400 hover:text-amber-300 border border-amber-900 px-2 py-1">
            ↳ {e.own_domain}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Tvoja doména *</label>
        <input type="text" required value={form.own_domain}
          onChange={(e) => setForm({ ...form, own_domain: e.target.value })}
          placeholder="napr. tvojeshop.sk"
          data-testid="manager-own-domain"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Tvoj pitch / pozicovanie</label>
        <textarea rows={2} value={form.own_pitch}
          onChange={(e) => setForm({ ...form, own_pitch: e.target.value })}
          placeholder="Čím sa odlišuješ"
          data-testid="manager-pitch"
          className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
      </div>
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Konkurencia (do 5)</label>
        {form.competitor_domains.map((c, i) => (
          <input key={i} type="text" value={c}
            onChange={(e) => {
              const arr = [...form.competitor_domains]; arr[i] = e.target.value;
              setForm({ ...form, competitor_domains: arr });
            }}
            placeholder={`konkurent #${i + 1}`}
            data-testid={`manager-competitor-${i}`}
            className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-1.5 text-sm focus:outline-none focus:border-amber-500 mb-1" />
        ))}
      </div>
      <button type="submit" disabled={status.loading || !form.own_domain.trim()}
        data-testid="manager-submit"
        className="w-full bg-amber-500 hover:bg-amber-400 text-black font-black py-3 text-[12px] uppercase tracking-widest disabled:opacity-40">
        {status.loading ? <Loader2 className="w-4 h-4 inline animate-spin" /> : <Crown className="w-4 h-4 inline mr-2" />}
        {status.loading ? "Analyzujem konkurenciu…" : "Predbehnúť konkurenciu"}
      </button>
      {status.error && <p className="text-sm text-red-400" data-testid="manager-error">⚠️ {status.error}</p>}
    </form>
  );

  const outputPanel = (
    <div className="p-6 bg-neutral-950 border border-gray-800 space-y-4" data-testid="manager-output">
      {!result && !status.loading && (
        <div className="text-center text-gray-500 py-16">
          <Swords className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Zadaj seba + až 5 konkurentov a získaj akčný plán ako byť #1.</p>
        </div>
      )}
      {result && (
        <>
          {result.executive_summary && (
            <div className="border border-amber-500/40 bg-amber-950/20 p-3">
              <p className="text-[10px] font-black text-amber-300 uppercase tracking-widest mb-1">Zhrnutie stratégie</p>
              <p className="text-xs text-gray-200">{result.executive_summary}</p>
            </div>
          )}
          {result.comparison_matrix?.length > 0 && (
            <div data-testid="result-matrix">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2">Porovnanie</p>
              <div className="space-y-2">
                {result.comparison_matrix.map((row, i) => (
                  <div key={i} className="border border-gray-900 p-2 text-xs">
                    <p className="font-bold text-white">{row.competitor}</p>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div>
                        <p className="text-[10px] text-emerald-400 font-bold">Silné stránky</p>
                        <ul className="text-gray-300">{(row.strengths || []).map((s, j) => <li key={j}>· {s}</li>)}</ul>
                      </div>
                      <div>
                        <p className="text-[10px] text-red-400 font-bold">Slabé stránky</p>
                        <ul className="text-gray-300">{(row.weaknesses || []).map((w, j) => <li key={j}>· {w}</li>)}</ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.weak_spots?.length > 0 && (
            <div data-testid="result-weak-spots">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2"><Target className="w-3 h-3 inline mr-1" /> Kde udrieť</p>
              <ul className="space-y-2">
                {result.weak_spots.map((w, i) => (
                  <li key={i} className="border border-red-900/40 bg-red-950/10 p-2 text-xs">
                    <p className="text-red-300 font-bold">{w.competitor}: {w.weakness}</p>
                    <p className="text-gray-300 mt-1">→ {w.how_to_exploit}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.action_plan?.length > 0 && (
            <div data-testid="result-action-plan">
              <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-2">Akčný plán</p>
              <ol className="space-y-1.5">
                {result.action_plan.sort((a, b) => (a.priority || 0) - (b.priority || 0)).map((a, i) => (
                  <li key={i} className="text-xs border border-gray-900 p-2">
                    <span className="inline-block w-6 h-6 leading-6 text-center bg-amber-500 text-black font-black text-[10px] mr-2">#{a.priority}</span>
                    <span className="text-white font-bold">{a.move}</span>
                    <p className="text-gray-400 mt-1 ml-8">{a.effort_days}d · {a.expected_impact}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {result.used_fallback && (
            <p className="text-[10px] text-gray-600 italic">ⓘ Použili sme rezervné odporúčania (LLM nedostupný).</p>
          )}
        </>
      )}
    </div>
  );

  return (
    <AgentOsDemoShell
      testid="agent-demo-manager"
      h1="Agent Manager"
      lead="Porovnáva konkurenciu, nájde slabiny, posiela akčný plán. Musíme byť #1 — táto je manažérka, ktorá to spočíta."
      left={formPanel}
      right={outputPanel}
    />
  );
}
